import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { listBlogPosts, type BlogPost } from "@/lib/api/blog.functions";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { hreflangLinks } from "@/lib/hreflang";

export const Route = createFileRoute("/blog/")({
  loader: async (): Promise<{ posts: BlogPost[] }> => {
    const { posts } = await listBlogPosts({ data: {} });
    return { posts };
  },
  head: () => ({
    meta: [
      { title: "Blog — Family Organising Guides | Checkbloom" },
      {
        name: "description",
        content:
          "Practical checklists and guides for busy households — pregnancy, moving, holidays, illness, and more. Save any guide as a working checklist in Checkbloom.",
      },
      { property: "og:title", content: "Blog — Family Organising Guides | Checkbloom" },
      {
        property: "og:description",
        content:
          "Practical, sensitive guides for the moments that matter — written for real families.",
      },
      { property: "og:url", content: "https://checkbloom.app/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://checkbloom.app/blog" }, ...hreflangLinks("https://checkbloom.app/blog")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Checkbloom Blog",
          url: "https://checkbloom.app/blog",
          description:
            "Family organising guides — practical checklists for life's busy moments.",
        }),
      },
    ],
  }),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
  component: BlogIndex,
});

function BlogIndex() {
  const { posts } = Route.useLoaderData() as { posts: BlogPost[] };
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);

  const categories = useMemo(
    () => Array.from(new Set(posts.map((p) => p.category))).sort(),
    [posts],
  );
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return posts.filter((p) => {
      if (cat && p.category !== cat) return false;
      if (!t) return true;
      return (
        p.title.toLowerCase().includes(t) ||
        (p.excerpt ?? "").toLowerCase().includes(t) ||
        p.tags.some((tg) => tg.toLowerCase().includes(t))
      );
    });
  }, [posts, q, cat]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <header className="mb-10 text-center">
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            The Checkbloom Blog
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            Practical, sensitive guides for life's busy moments — turn any guide into a working
            checklist with one tap.
          </p>
        </header>

        <p className="mb-6 text-center text-sm text-muted-foreground">
          Blog posts are currently available in English only.
        </p>

        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search guides…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCat(null)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                !cat ? "border-primary bg-primary text-primary-foreground" : "border-border"
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c === cat ? null : c)}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  c === cat
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:bg-muted"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="py-20 text-center text-sm text-muted-foreground">
            No posts yet — check back soon.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-lg"
    >
      {post.featured_image_url ? (
        <img
          src={post.featured_image_url}
          alt={post.title}
          loading="lazy"
          className="aspect-[16/9] w-full object-cover"
        />
      ) : (
        <div className="aspect-[16/9] w-full bg-gradient-to-br from-primary/15 to-accent/15" />
      )}
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary">{post.category}</Badge>
          <span>·</span>
          <span>{post.reading_time_minutes} min read</span>
        </div>
        <h2 className="font-display text-lg font-semibold leading-snug text-foreground group-hover:text-primary">
          {post.title}
        </h2>
        {post.excerpt && (
          <p className="line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
        )}
      </div>
    </Link>
  );
}
